import { FC, useMemo, useState } from 'react';

import { usersQueryKeys } from '@apis/usersApi';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { AutocompleteWithQuery } from '@shared/components/autocomplete-with-query/AutocompleteWithQuery';
import { Avatar } from '@shared/components/avatar';
import { Flex } from '@shared/components/flex';
import { ListUserItem } from '@shared/models/user';
import { LookupUserItem } from '@shared/types/common/common';
import { listUserToLookupUser } from '@shared/utils/lookup';

import { styles } from './UserAutocomplete.styles';

export interface UserAutocompleteInterface extends WithStyles<typeof styles> {
  autoFocus?: boolean;
  dropUp?: boolean;
  initialUser?: LookupUserItem;
  label?: string;
  withLocalSelecting?: boolean;
  setUser: (user?: LookupUserItem) => void;
}

const UserAutocompleteComponent: FC<UserAutocompleteInterface> = ({
  autoFocus = true,
  classes,
  dropUp = true,
  initialUser,
  label,
  setUser,
  withLocalSelecting = true,
}) => {
  const [searchField, setSearchField] = useState<string>(initialUser?.name || '');
  const [selectedUser, setSelectedUser] = useState<LookupUserItem | undefined>(initialUser);

  const handleUserSearchChange = (search: string) => {
    setSearchField(search);

    if (!search) {
      setSelectedUser(undefined);
    }
  };

  const handleSelect = (user: LookupUserItem | null) => {
    if (user) {
      if (withLocalSelecting) {
        setSelectedUser(user);
        setSearchField(user.name);
      } else {
        setSearchField('');
      }
      setUser(user);

      handleClearAutocomplete();
    }
  };

  const handleClearAutocomplete = (iconClick?: boolean) => {
    if (iconClick) {
      setSelectedUser(undefined);
      setUser();
    }
  };

  const renderUserOption = (user: ListUserItem) => {
    return (
      <Flex alignItems="center" className={classes.option} wrap="nowrap">
        <Avatar
          classes={{ root: classes.optionAvatar }}
          image={user.image?.url ?? ''}
          name={user.name}
          size={24}
          theme={user.theme}
        />
        <span className={classes.userName}>{user.name}</span>
      </Flex>
    );
  };

  const userIcon = useMemo(() => {
    if (selectedUser) {
      return (
        <Avatar
          classes={{ root: classes.avatar }}
          image={selectedUser.image}
          name={selectedUser.name}
          size={24}
          theme={selectedUser.theme}
        />
      );
    }

    return <div className={classes.avatarPlaceholder} />;
  }, [selectedUser]);

  return (
    <Flex autoWidth={false} classes={{ root: classes.root }}>
      {label && (
        <Flex autoWidth={false} classes={{ root: classes.label }}>
          {label}
        </Flex>
      )}
      <div className={classes.searchField}>
        <AutocompleteWithQuery
          classes={{ optionsList: classes.autocompleteList }}
          dropUp={dropUp}
          inputProps={{
            placeholder: t('select_a_user'),
            autoFocus: autoFocus,
            InputProps: {
              startAdornment: userIcon,
            },
          }}
          queryFactory={usersQueryKeys.list}
          searchValue={searchField}
          withClearIcon={true}
          onSelect={(user) => handleSelect(listUserToLookupUser(user))}
          onSearchValueChange={handleUserSearchChange}
          renderOption={renderUserOption}
        />
      </div>
    </Flex>
  );
};

export const UserAutocomplete = withStyles(styles)(UserAutocompleteComponent);
